import {
  Rectangle, SizeSchema, useClientContext,
  TransformData, PlaygroundContext, FlowNodeEntity,
} from '@flowgram.ai/free-layout-editor';
// import useChatStore from '$/store/useChatStore';

const LEFT_BUTTON_WIDTH = 0;
const SIDEBAR_WIDTH = 390;

/** 较长边占有效视口对应边的比例（80%） */
const FIT_ZOOM_LONG_EDGE_VIEWPORT_RATIO = 0.8;

/**
 * @description 根据内容边界较长边占视口对应边 80% 计算缩放：宽≥高时用视口有效宽度，否则用视口高度。
 * @param paddedBounds - 已加 padding 的内容矩形
 * @param viewportWidth - 有效视口宽度（已扣除侧边栏等）
 * @param viewportHeight - 视口高度
 * @returns 缩放值，长边在视口上的投影约为对应维度的 80%
 */
function computeZoomByLongEdgeFitsViewportRatio(
  paddedBounds: Rectangle,
  viewportWidth: number,
  viewportHeight: number,
): number {
  const bw = Math.max(paddedBounds.width, 1e-6);
  const bh = Math.max(paddedBounds.height, 1e-6);
  const zoomW = (FIT_ZOOM_LONG_EDGE_VIEWPORT_RATIO * viewportWidth) / bw;
  const zoomH = (FIT_ZOOM_LONG_EDGE_VIEWPORT_RATIO * viewportHeight) / bh;
  return Math.min(zoomW, zoomH, 4);
}

export const fitViewCustom = (
  ctx: PlaygroundContext,
  config?: { buttonOffset?: number; sideBarWidth?: number; sideBarCollapsed?: boolean; rightOffset?: number; },
) => {
//   const { sideBarWidth, sideBarCollapsed } = useChatStore.getState();
  const sideBarWidth = config?.sideBarWidth ?? SIDEBAR_WIDTH;
  const sideBarCollapsed = config?.sideBarCollapsed ?? false;
  const easing = true;
  const easingDuration = 300;
  const padding = 30; // 四周的内边距
  const buttonOffset = config?.buttonOffset ?? 72; // 左侧按钮的宽度
  const rightOffset = config?.rightOffset ?? 0; // 右侧边距

  const bounds = Rectangle.enlarge(ctx.document.getAllNodes().map((node: FlowNodeEntity) => node.getData<TransformData>(TransformData).bounds));
  const allViewport = ctx.playground.config.getViewport();
  const { zoom } = ctx.playground.config;
  const {
    x, y, width, height,
  } = allViewport;
  const xWithZoom = x * zoom;
  const yWithZoom = y * zoom;
  const widthWithZoom = width * zoom;
  const heightWithZoom = height * zoom;
  const _sideBarWidth = sideBarCollapsed ? 0 : (sideBarWidth + buttonOffset + rightOffset);
  const viewportRectangle = new Rectangle(
    xWithZoom,
    yWithZoom,
    widthWithZoom - _sideBarWidth,
    heightWithZoom,
  );
  const finalBounds = bounds.clone();
  const _zoom = SizeSchema.fixSize(finalBounds.pad(padding, padding), viewportRectangle);
  ctx.playground.scrollToView({
    bounds: finalBounds,
    zoom: _zoom,
    easing,
    easingDuration,
    scrollToCenter: true,
    scrollDelta: {
      x: -((_sideBarWidth) / _zoom / 2) + (rightOffset / _zoom),
      y: 0,
    },
  });
};

export default function useFitView(config: { buttonOffset?: number; sideBarWidth?: number; sideBarCollapsed?: boolean; rightOffset?: number; } = {}): {
  fitView: () => void;
} {
  const ctx = useClientContext();

  return {
    fitView: () => {
      fitViewCustom(ctx, config);
    },
  };
}

/**
 *
 * @param ctx - 画布上下文
 * @param bounds - 需要进入视口的边界
 * @param sidebarWidth - 侧边栏宽度，默认 390 + 72 390的宽度 和 72的按钮宽度
 * @param fitZoom - 为 true 时按「较长边占视口对应边 60%」计算缩放；为 false 时用 `SizeSchema.fixSize` 完整装入视口
 */
export const nodesIntoView = ({
  ctx, bounds, sidebarWidth = (SIDEBAR_WIDTH + LEFT_BUTTON_WIDTH), sideBarCollapsed = false, fitZoom = false,
}: { ctx: PlaygroundContext; bounds: Rectangle; sidebarWidth?: number; sideBarCollapsed?: boolean; fitZoom?: boolean; }) => {
  const _sidebarWidth = sideBarCollapsed ? 0 : (sidebarWidth);
  const padding = 30;
  const {
    x, y, width, height,
  } = ctx.playground.config.getViewport(false);
  const viewportRectangle = new Rectangle(
    x,
    y,
    width - _sidebarWidth,
    height,
  );
  /**
   * `Rectangle.pad` / `SizeSchema.fixSize` 会原地改写矩形；与 {@link fitViewCustom} 一样先 `clone` 再 `pad`，
   * 避免修改调用方持有的 `bounds`。
   */
  const paddedBounds = bounds.clone().pad(padding, padding);
  let _zoom = SizeSchema.fixSize(paddedBounds, viewportRectangle);
  if (fitZoom) {
    _zoom = computeZoomByLongEdgeFitsViewportRatio(
      paddedBounds,
      width - _sidebarWidth,
      height,
    );
  }
  ctx.playground.scrollToView({
    bounds: bounds.clone(),
    scrollDelta: {
      x: -(_sidebarWidth / 2 / _zoom),
      y: 0,
    },
    zoom: _zoom,
    scrollToCenter: true,
    easingDuration: 500,
  });
};
